/**
 * LARCIN — Fixed Bundle Builder (assets/larcin-fixed-bundle.js)
 *
 * WHAT GOES IN THE CART
 * ---------------------
 * Exactly one line: the parent bundle product's variant, quantity 1, at
 * whatever price Shopify has on that variant. The seven component picks
 * are attached to that line as properties. Nothing here ever adds a
 * component variant to the cart, so the fixed price cannot be gamed from
 * the client and no component can leak into the cart as its own line.
 *
 * Two properties are customer-visible (they render under the line in the
 * cart, at checkout, in the confirmation email and on the packing slip):
 *
 *   Full-size (50ml) : Vice City, Vermilion, Old Money
 *   Samples (5ml)    : Brown Sugar, Aristocrat, Pacifica, Heaven's Cut
 *
 * The rest are underscore-prefixed, which hides them from the customer
 * while leaving them readable on the order in the Shopify admin and to
 * Shopify Flow:
 *
 *   _bundle_id     unique per add, so two different bundles never merge
 *   _bundle_type   Flow keys off this to decrement component inventory
 *   _components    JSON: variant id, sku, title, size and qty per pick
 *
 * VALIDATION
 * ----------
 * Immediately before adding, every selected variant is re-checked against
 * the live product JSON. A fragrance that sold out mid-session blocks the
 * add and is flagged on its card rather than silently shipping short.
 *
 * PERSISTENCE
 * -----------
 * Selections survive a reload and a return visit via sessionStorage, so
 * "go back and change your picks" costs the customer nothing.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

const STAGES = ['full', 'sample'];

class LarcinFixedBundle extends HTMLElement {
  /** @type {{full: Map<string, number>, sample: Map<string, number>}} */
  #sel = { full: new Map(), sample: new Map() };
  /** @type {Map<string, HTMLElement>} */
  #cards = new Map();
  #config = null;
  #fmt = null;
  #stage = 'full';
  #submitting = false;
  #query = '';
  #toastTimer = null;
  #observer = null;

  connectedCallback() {
    const raw = this.querySelector('[data-lfb-config]')?.textContent;
    try {
      this.#config = JSON.parse(raw || '{}');
    } catch {
      console.error('[larcin-fixed-bundle] config parse failed');
      return;
    }

    this.#fmt = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      style: 'currency',
      currency: this.#config.currency || 'AUD',
    });

    this.#indexCards();
    this.#bind();
    this.#restore();
    this.#watchScroll();
    this.#render();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    clearTimeout(this.#toastTimer);
  }

  /* ================= setup ================= */

  #indexCards() {
    for (const card of this.querySelectorAll('[data-lfb-card]')) {
      this.#cards.set(`${card.dataset.stage}:${card.dataset.variantId}`, card);
    }
  }

  #card(stage, variantId) {
    return this.#cards.get(`${stage}:${variantId}`) || null;
  }

  #limit(stage) {
    return stage === 'full' ? Number(this.#config.fullCount) : Number(this.#config.sampleCount);
  }

  #chosen(stage) {
    let n = 0;
    for (const q of this.#sel[stage].values()) n += q;
    return n;
  }

  #remaining(stage) {
    return Math.max(0, this.#limit(stage) - this.#chosen(stage));
  }

  #isComplete() {
    return STAGES.every((s) => this.#remaining(s) === 0);
  }

  /* ================= events ================= */

  #bind() {
    // Stage tabs
    for (const tab of this.querySelectorAll('[data-lfb-stage-tab]')) {
      tab.addEventListener('click', () => this.#setStage(tab.dataset.lfbStageTab, true));
    }

    // Card interactions. Delegated so both grids share one listener and
    // nothing needs rebinding when cards are filtered.
    this.addEventListener('click', (event) => {
      const card = event.target.closest?.('[data-lfb-card]');
      if (!card || !this.contains(card)) return;

      if (event.target.closest('[data-lfb-dec]')) {
        event.preventDefault();
        this.#change(card, -1);
        return;
      }
      if (event.target.closest('[data-lfb-inc]') || event.target.closest('[data-lfb-select]')) {
        event.preventDefault();
        this.#change(card, +1);
        return;
      }
      // Tapping the card body selects, but only while it has room.
      if (!card.classList.contains('is-selected')) this.#change(card, +1);
    });

    // Keyboard: cards are list items, so give the select button the work.
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const slotX = event.target.closest?.('[data-lfb-slot-remove]');
      if (slotX) {
        event.preventDefault();
        slotX.click();
      }
    });

    // Slot removal
    this.addEventListener('click', (event) => {
      const btn = event.target.closest?.('[data-lfb-slot-remove]');
      if (!btn || !this.contains(btn)) return;
      event.preventDefault();
      const { stage, variantId } = btn.dataset;
      const card = this.#card(stage, variantId);
      if (card) this.#change(card, -1);
    });

    // Search
    const search = this.querySelector('[data-lfb-search]');
    let debounce;
    search?.addEventListener('input', (event) => {
      clearTimeout(debounce);
      const value = event.target.value;
      debounce = setTimeout(() => {
        this.#query = this.#normalize(value);
        this.#filter();
      }, 140);
    });

    // Mobile sheet
    const sheet = this.querySelector('[data-lfb-sheet]');
    this.querySelector('[data-lfb-open-sheet]')?.addEventListener('click', () => sheet?.showModal());
    this.querySelector('[data-lfb-close-sheet]')?.addEventListener('click', () => sheet?.close());
    sheet?.addEventListener('click', (event) => {
      if (event.target === sheet) sheet.close();
    });

    // Add to cart — every copy of the button drives the same handler.
    for (const button of this.querySelectorAll('[data-lfb-atc]')) {
      button.addEventListener('click', () => this.#addToCart());
    }
  }

  #watchScroll() {
    const hero = this.querySelector('.lfb-hero');
    const bar = this.querySelector('[data-lfb-bar]');
    if (!hero || !bar) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting;
        bar.classList.toggle('is-visible', show);
        this.toggleAttribute('data-bar-visible', show);
      },
      { rootMargin: '0px 0px -40% 0px' }
    );
    this.#observer.observe(hero);
  }

  /* ================= selection ================= */

  #change(card, delta) {
    if (card.dataset.soldout === 'true') {
      this.#toast(this.#config.text.soldOut, true);
      return;
    }

    const stage = card.dataset.stage;
    const id = card.dataset.variantId;
    const map = this.#sel[stage];
    const current = map.get(id) || 0;

    if (delta > 0) {
      if (this.#remaining(stage) === 0) {
        this.#toast(this.#config.text.stageFull, true);
        return;
      }
      const cap = Number(card.dataset.cap || 99);
      if (current > 0 && !this.#config.allowDuplicates) {
        this.#toast('You’ve already picked that one.', true);
        return;
      }
      if (current + 1 > cap) {
        this.#toast(`Only ${cap} of ${card.dataset.title} left in stock.`, true);
        return;
      }
      map.set(id, current + 1);
    } else {
      if (current <= 1) map.delete(id);
      else map.set(id, current - 1);
    }

    this.#persist();
    this.#render();

    // Once stage one is full, move the customer on. They can always tab back.
    if (delta > 0 && stage === 'full' && this.#remaining('full') === 0 && this.#remaining('sample') > 0) {
      this.#toast(this.#config.text.stageDone);
      setTimeout(() => this.#setStage('sample', true), 450);
    }
  }

  #setStage(stage, scroll = false) {
    if (!STAGES.includes(stage)) return;
    this.#stage = stage;
    this.dataset.stage = stage;

    for (const tab of this.querySelectorAll('[data-lfb-stage-tab]')) {
      tab.setAttribute('aria-selected', String(tab.dataset.lfbStageTab === stage));
    }
    for (const panel of this.querySelectorAll('[data-lfb-panel]')) {
      panel.hidden = panel.dataset.lfbPanel !== stage;
    }

    const search = this.querySelector('[data-lfb-search]');
    if (search) {
      search.value = '';
      this.#query = '';
    }
    this.#filter();
    this.#renderProgress();

    if (scroll) {
      const tabs = this.querySelector('.lfb-stages');
      tabs?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /* ================= rendering ================= */

  #render() {
    this.#renderCards();
    this.#renderSlots();
    this.#renderCounts();
    this.#renderProgress();
    this.#renderButtons();
  }

  #renderCards() {
    for (const stage of STAGES) {
      const locked = this.#remaining(stage) === 0;
      this.toggleAttribute(`data-${stage}-locked`, locked);

      for (const card of this.querySelectorAll(`[data-lfb-card][data-stage="${stage}"]`)) {
        const qty = this.#sel[stage].get(card.dataset.variantId) || 0;
        card.classList.toggle('is-selected', qty > 0);

        const badge = card.querySelector('[data-lfb-qty-badge]');
        if (badge) badge.textContent = String(qty);
        const out = card.querySelector('[data-lfb-qty-out]');
        if (out) out.value = String(qty);

        const inc = card.querySelector('[data-lfb-inc]');
        if (inc) {
          const cap = Number(card.dataset.cap || 99);
          inc.disabled = locked || qty >= cap || !this.#config.allowDuplicates;
        }
      }
    }
  }

  #renderSlots() {
    for (const stage of STAGES) {
      const limit = this.#limit(stage);
      const picks = [];
      for (const [id, qty] of this.#sel[stage]) {
        for (let i = 0; i < qty; i += 1) picks.push(id);
      }

      const html = [];
      for (let i = 0; i < limit; i += 1) {
        const id = picks[i];
        const card = id ? this.#card(stage, id) : null;

        if (card) {
          const thumb = card.dataset.thumb;
          html.push(
            `<li class="lfb-slot is-filled">
               <span class="lfb-slot__box">
                 ${thumb ? `<img class="lfb-slot__img" src="${thumb}" alt="" loading="lazy" decoding="async">` : ''}
                 <button type="button" class="lfb-slot__x" data-lfb-slot-remove data-stage="${stage}" data-variant-id="${id}"
                         aria-label="Remove ${this.#escape(card.dataset.title)} from your bundle">
                   <svg viewBox="0 0 10 10" aria-hidden="true"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
                 </button>
               </span>
               <span class="lfb-slot__name">${this.#escape(card.dataset.title)}</span>
             </li>`
          );
        } else {
          const isNext = i === picks.length;
          html.push(
            `<li class="lfb-slot${isNext ? ' is-next' : ''}">
               <span class="lfb-slot__box"><span class="lfb-slot__plus" aria-hidden="true">+</span></span>
               <span class="lfb-slot__name">Slot ${i + 1}</span>
             </li>`
          );
        }
      }

      for (const list of this.querySelectorAll(`[data-lfb-slots="${stage}"]`)) {
        list.innerHTML = html.join('');
      }
    }
  }

  #renderCounts() {
    for (const stage of STAGES) {
      const chosen = this.#chosen(stage);
      const limit = this.#limit(stage);

      for (const el of this.querySelectorAll(`[data-lfb-tab-count="${stage}"]`)) {
        el.textContent = `${chosen} of ${limit} selected`;
      }
      for (const el of this.querySelectorAll(`[data-lfb-group-count="${stage}"]`)) {
        el.textContent = `${chosen}/${limit}`;
      }
      const tab = this.querySelector(`[data-lfb-stage-tab="${stage}"]`);
      tab?.classList.toggle('is-complete', chosen === limit);
    }

    const total = this.#chosen('full') + this.#chosen('sample');
    const totalLimit = this.#limit('full') + this.#limit('sample');
    const status = this.querySelector('[data-lfb-bar-status]');
    if (status) status.textContent = `${total} of ${totalLimit} chosen — view bundle`;
  }

  #renderProgress() {
    const total = this.#chosen('full') + this.#chosen('sample');
    const totalLimit = this.#limit('full') + this.#limit('sample');

    const fill = this.querySelector('[data-lfb-progress-fill]');
    if (fill) fill.style.inlineSize = `${Math.round((total / totalLimit) * 100)}%`;

    this.toggleAttribute('data-complete', this.#isComplete());

    const msg = this.querySelector('[data-lfb-progress-msg]');
    if (!msg) return;

    const stage = this.#stage;
    const left = this.#remaining(stage);
    const name = stage === 'full' ? this.#config.labels.full : this.#config.labels.sample;

    if (this.#isComplete()) {
      msg.innerHTML = `<strong>Bundle complete.</strong> Add it to your cart below.`;
    } else if (left === 0) {
      const other = stage === 'full' ? 'sample' : 'full';
      const otherName = other === 'full' ? this.#config.labels.full : this.#config.labels.sample;
      msg.innerHTML = `This stage is done — <span class="lfb-hl">${this.#remaining(other)} ${this.#escape(otherName)}</span> still to choose.`;
    } else {
      msg.innerHTML = `Choose <span class="lfb-hl">${left} more ${this.#escape(name)}${left === 1 ? '' : 's'}</span>.`;
    }
  }

  #renderButtons() {
    const complete = this.#isComplete();
    const outstanding = this.#remaining('full') + this.#remaining('sample');
    const label = complete
      ? this.#config.text.atcReady
      : String(this.#config.text.atcIncomplete).replace('[n]', String(outstanding));

    for (const button of this.querySelectorAll('[data-lfb-atc]')) {
      button.disabled = !complete || this.#submitting;
      if (!button.hasAttribute('data-busy')) button.textContent = label;
    }
  }

  /* ================= search ================= */

  #filter() {
    const grid = this.querySelector(`[data-lfb-grid="${this.#stage}"]`);
    if (!grid) return;

    const q = this.#query;
    let visible = 0;

    for (const card of grid.querySelectorAll('[data-lfb-card]')) {
      const hay = this.#normalize(
        [card.dataset.title, card.dataset.inspired, card.dataset.brand, card.dataset.family].join(' ')
      );
      const match = !q || hay.includes(q);
      card.toggleAttribute('data-filtered-out', !match);
      if (match) visible += 1;
    }

    this.toggleAttribute('data-empty', visible === 0);
  }

  #normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  #escape(value) {
    return String(value || '').replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }

  /* ================= persistence ================= */

  #storageKey() {
    return `lfb:${this.dataset.sectionId}:${this.#config.bundleVariantId}`;
  }

  #persist() {
    try {
      const payload = {
        full: Array.from(this.#sel.full.entries()),
        sample: Array.from(this.#sel.sample.entries()),
      };
      sessionStorage.setItem(this.#storageKey(), JSON.stringify(payload));
    } catch {
      /* private mode — selections simply won't survive a reload */
    }
  }

  #restore() {
    let payload = null;

    // A ?lfb= parameter wins, so an "edit your bundle" link can rehydrate
    // a bundle that is already sitting in the cart.
    const param = new URLSearchParams(location.search).get('lfb');
    if (param) {
      try {
        payload = JSON.parse(atob(param));
      } catch {
        payload = null;
      }
    }

    if (!payload) {
      try {
        payload = JSON.parse(sessionStorage.getItem(this.#storageKey()) || 'null');
      } catch {
        payload = null;
      }
    }
    if (!payload) return;

    for (const stage of STAGES) {
      for (const [id, qty] of payload[stage] || []) {
        const card = this.#card(stage, String(id));
        // Silently drop anything that has since sold out or left the collection.
        if (!card || card.dataset.soldout === 'true') continue;
        const capped = Math.min(Number(qty) || 0, Number(card.dataset.cap || 99));
        if (capped > 0) this.#sel[stage].set(String(id), capped);
      }
      // Trim if the merchant has since reduced the required count.
      while (this.#chosen(stage) > this.#limit(stage)) {
        const lastId = Array.from(this.#sel[stage].keys()).pop();
        const q = this.#sel[stage].get(lastId);
        if (q > 1) this.#sel[stage].set(lastId, q - 1);
        else this.#sel[stage].delete(lastId);
      }
    }

    if (this.#remaining('full') === 0 && this.#remaining('sample') > 0) this.#setStage('sample');
  }

  #stateParam() {
    try {
      return btoa(
        JSON.stringify({
          full: Array.from(this.#sel.full.entries()),
          sample: Array.from(this.#sel.sample.entries()),
        })
      );
    } catch {
      return '';
    }
  }

  /* ================= validation ================= */

  /**
   * Re-checks every selected variant against the live product JSON. Returns
   * an array of cards that can no longer be fulfilled. A network failure is
   * not treated as a sell-out: Shopify still refuses genuinely unavailable
   * inventory at checkout, and blocking a good customer on a flaky request
   * would cost more than it saves.
   */
  async #validate() {
    const wanted = [];
    for (const stage of STAGES) {
      for (const [id, qty] of this.#sel[stage]) {
        const card = this.#card(stage, id);
        if (card) wanted.push({ card, id: Number(id), qty });
      }
    }

    const handles = [...new Set(wanted.map((w) => w.card.dataset.handle))];
    const byVariant = new Map();

    await Promise.all(
      handles.map(async (handle) => {
        try {
          const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
          if (!res.ok) return;
          const data = await res.json();
          for (const variant of data.variants || []) byVariant.set(variant.id, variant);
        } catch {
          /* leave unknown — treated as available below */
        }
      })
    );

    const problems = [];
    for (const { card, id, qty } of wanted) {
      const live = byVariant.get(id);
      if (!live) continue;
      const enough =
        live.available &&
        (live.inventory_management !== 'shopify' ||
          live.inventory_policy !== 'deny' ||
          (live.inventory_quantity ?? 0) >= qty);
      if (!enough) problems.push(card);
    }
    return problems;
  }

  #flagSoldOut(cards) {
    for (const card of cards) {
      card.dataset.soldout = 'true';
      card.setAttribute('aria-disabled', 'true');
      const stock = card.querySelector('[data-lfb-stock]');
      if (stock) {
        stock.innerHTML = `<span class="lfb-card__dot lfb-card__dot--out" aria-hidden="true"></span>Just sold out`;
      }
      const stage = card.dataset.stage;
      this.#sel[stage].delete(card.dataset.variantId);
    }
    cards[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.#persist();
    this.#render();
  }

  /* ================= add to cart ================= */

  async #addToCart() {
    if (this.#submitting || !this.#isComplete()) return;

    this.#submitting = true;
    const buttons = Array.from(this.querySelectorAll('[data-lfb-atc]'));
    buttons.forEach((b) => {
      b.disabled = true;
      b.setAttribute('data-busy', '');
    });

    try {
      const problems = await this.#validate();
      if (problems.length) {
        this.#toast(this.#config.text.soldOutError, true);
        this.#flagSoldOut(problems);
        return;
      }

      const bundleId = `LFB-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`.toUpperCase();
      const properties = this.#buildProperties(bundleId);

      const item = {
        id: Number(this.#config.bundleVariantId),
        quantity: 1,
        properties,
      };

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'larcin-fixed-bundle',
          lines: [{ merchandiseId: String(item.id), quantity: 1 }],
          promise: deferred.promise,
        })
      );

      const cartAddUrl =
        (typeof Theme !== 'undefined' && Theme?.routes?.cart_add_url) || '/cart/add.js';

      const res = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item], sections: sectionIds.join(',') }),
      });
      const data = await res.json();

      if (!res.ok || data.status) {
        const message = data.description || data.message || this.#config.text.cartError;
        this.#toast(message, true);
        const cart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            source: 'larcin-fixed-bundle',
            sourceId: bundleId,
            itemCount: 0,
          },
        });
        return;
      }

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'larcin-fixed-bundle',
          sourceId: bundleId,
          itemCount: 1,
          sections: data.sections,
        },
      });

      this.#toast(this.#config.text.added);
      this.#sel.full.clear();
      this.#sel.sample.clear();
      try {
        sessionStorage.removeItem(this.#storageKey());
      } catch {
        /* noop */
      }
      this.#setStage('full');
    } catch (error) {
      console.error('[larcin-fixed-bundle] cart error', error);
      this.#toast(this.#config.text.cartError, true);
    } finally {
      this.#submitting = false;
      buttons.forEach((b) => b.removeAttribute('data-busy'));
      this.querySelector('[data-lfb-sheet]')?.close();
      this.#render();
    }
  }

  /**
   * Two customer-visible properties carrying readable names, and a set of
   * underscore-prefixed ones carrying machine detail for fulfilment and
   * for the inventory Flow.
   */
  #buildProperties(bundleId) {
    const components = [];
    const names = { full: [], sample: [] };

    for (const stage of STAGES) {
      for (const [id, qty] of this.#sel[stage]) {
        const card = this.#card(stage, id);
        if (!card) continue;
        for (let i = 0; i < qty; i += 1) names[stage].push(card.dataset.title);
        components.push({
          stage,
          variant_id: Number(id),
          product_id: Number(card.dataset.productId),
          handle: card.dataset.handle,
          sku: card.dataset.sku || '',
          title: card.dataset.title,
          size: card.dataset.size,
          quantity: qty,
        });
      }
    }

    const editUrl = `/products/${this.#config.bundleHandle}?lfb=${this.#stateParam()}`;

    return {
      [this.#config.labels.fullProperty]: names.full.join(', '),
      [this.#config.labels.sampleProperty]: names.sample.join(', '),
      _bundle_id: bundleId,
      _bundle_type: this.#config.bundleType,
      _bundle_components: String(components.length),
      _components: JSON.stringify(components),
      _edit_url: editUrl,
    };
  }

  async #fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  /* ================= toast ================= */

  #toast(message, isError = false) {
    const toast = this.querySelector('[data-lfb-toast]');
    const text = this.querySelector('[data-lfb-toast-text]');
    if (!toast || !text) return;

    text.textContent = message;
    toast.classList.toggle('lfb-toast--error', Boolean(isError));
    toast.classList.add('is-on');
    clearTimeout(this.#toastTimer);
    this.#toastTimer = setTimeout(() => toast.classList.remove('is-on'), 2600);
  }
}

if (!customElements.get('larcin-fixed-bundle')) {
  customElements.define('larcin-fixed-bundle', LarcinFixedBundle);
}
